import React, { useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import newLogo from "../../assets/logo/logo.svg";
import QuickSightBeta from "../../assets/logo/QuickSight.png";
import { AiOutlineLogout } from "react-icons/ai";
import { HiOutlineMenu } from "react-icons/hi";
import { APP_HOME } from "../../routes/paths";

import NavMenu from "./NavMenu";

const NavBar = () => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [isAuthenticated, setIsAuthenticated] = React.useState(false);
    const [token, setToken] = React.useState<string | null>(localStorage.getItem("token"));
    const dropdownRef = useRef<HTMLUListElement>(null);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

    const firstName = localStorage.getItem("first_name");
    const lastName = localStorage.getItem("last_name");

    // Share-link guests came to review one video, so they don't get the marketing menu.
    const isGuestView = !isAuthenticated && pathname.startsWith("/shared/");

    React.useEffect(() => {
        if (token) {
            setIsAuthenticated(true);
        }
    }, [token]);

    const handleSignOut = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("refresh");
        localStorage.removeItem("first_name");
        localStorage.removeItem("last_name");
        localStorage.removeItem("email");
        setIsAuthenticated(false);
        navigate("/login");
    };

    const closeDropdown = () => {
        if (dropdownRef.current) {
            dropdownRef.current.blur();
        }
    };

    const handleMenuClick = (action: () => void) => {
        closeDropdown();
        action();
    };

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    return (
        <div className="navbar relative z-10 bg-base-100 shadow-md md:px-24 mb-10">
            <div className="navbar-start">
                <a className="font-medium text-2xl hover:cursor-pointer">
                    <img
                        src={QuickSightBeta}
                        alt="logo"
                        className="h-8"
                        onClick={() => navigate(isAuthenticated ? APP_HOME : "/")}
                    />
                </a>
                {isAuthenticated && (
                    <ul className="menu menu-horizontal px-1 ml-8 gap-5 font-medium hidden lg:flex">
                        <li>
                            <a onClick={() => navigate(APP_HOME)}>Home</a>
                        </li>
                    </ul>
                )}
            </div>

            <div className="navbar-center hidden lg:flex">
                {!isAuthenticated && !isGuestView && <NavMenu />}
            </div>

            <div className="navbar-end">
                {isAuthenticated ? (
                    <>
                        <div className="hidden lg:flex items-center">
                            <div className="dropdown dropdown-end">
                                <div
                                    tabIndex={0}
                                    role="button"
                                    className="btn btn-circle avatar placeholder"
                                >
                                    <div className="bg-[#485464] text-white rounded-full w-16">
                                        <span className="text-lg">{firstName?.charAt(0)}</span>
                                    </div>
                                </div>
                                <ul
                                    ref={dropdownRef}
                                    tabIndex={0}
                                    className="mt-4 z-[11] p-3 outline outline-gray-200 menu menu-md dropdown-content bg-base-100 rounded-box w-52 gap-3"
                                >
                                    <li className="truncate w-full">
                                        <p className="font-bold">
                                            {firstName} {lastName}
                                        </p>
                                    </li>
                                    <hr />
                                    <li>
                                        <a onClick={() => handleMenuClick(() => navigate("/company"))}>
                                            Company
                                        </a>
                                    </li>
                                    <li>
                                        <a onClick={() => handleMenuClick(() => navigate("/subscription"))}>
                                            Subscription
                                        </a>
                                    </li>
                                    <li>
                                        <a
                                            onClick={() =>
                                                handleMenuClick(() => navigate("/change-password"))
                                            }
                                        >
                                            Change password
                                        </a>
                                    </li>
                                    <li>
                                        <div
                                            className="text-red-500 flex"
                                            onClick={() => handleMenuClick(handleSignOut)}
                                        >
                                            <AiOutlineLogout size={16} color="red" />
                                            <a>Logout</a>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <div className="lg:hidden">
                            <button className="btn btn-ghost" onClick={toggleMobileMenu}>
                                <HiOutlineMenu className="h-6 w-6" />
                            </button>
                        </div>
                    </>
                ) : isGuestView ? (
                    <div className="flex items-center gap-2">
                        <Link className="btn btn-ghost btn-sm hidden sm:inline-flex" to="/login">
                            Sign in
                        </Link>
                        <Link className="btn btn-outline btn-sm" to="/signup">
                            Try Quicksight free
                        </Link>
                    </div>
                ) : (
                    <>
                        <div className="hidden lg:flex items-center gap-5">
                            <a className="btn" onClick={() => navigate("/login")}>Sign in</a>
                            <button className="btn btn-primary text-white" onClick={() => navigate("/signup")}>
                                Get Started For Free
                            </button>
                        </div>
                        <div className="lg:hidden">
                            <button className="btn btn-ghost" onClick={toggleMobileMenu}>
                                <HiOutlineMenu className="h-6 w-6" />
                            </button>
                        </div>
                    </>
                )}
            </div>

            {!isGuestView && (
                <div className={`lg:hidden fixed inset-0 bg-base-100 z-50 transition-transform duration-300 ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
                    <div className="p-4">
                        <div className="flex justify-between items-center mb-6">
                            <img src={QuickSightBeta} alt="logo" className="h-8" />
                            <button className="btn btn-ghost" onClick={toggleMobileMenu}>
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        
                        {isAuthenticated ? (
                            <div className="flex flex-col gap-4">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="bg-[#485464] text-white rounded-full w-12 h-12 flex items-center justify-center">
                                        <span className="text-lg">{firstName?.charAt(0)}</span>
                                    </div>
                                    <div>
                                        <p className="font-bold">{firstName} {lastName}</p>
                                    </div>
                                </div>
                                <a className="btn btn-ghost w-full justify-start" onClick={() => { toggleMobileMenu(); navigate(APP_HOME); }}>Home</a>
                                <a className="btn btn-ghost w-full justify-start" onClick={() => { toggleMobileMenu(); navigate("/company"); }}>Company</a>
                                <a className="btn btn-ghost w-full justify-start" onClick={() => { toggleMobileMenu(); navigate("/subscription"); }}>Subscription</a>
                                <button className="btn btn-ghost text-red-500 w-full justify-start" onClick={() => { toggleMobileMenu(); handleSignOut(); }}>
                                    <AiOutlineLogout size={16} color="red" />
                                    Logout
                                </button>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-4">
                                <NavMenu isMobile={true} onItemClick={toggleMobileMenu} />
                                <div className="mt-4 flex flex-col gap-3">
                                    <button className="btn w-full" onClick={() => { toggleMobileMenu(); navigate("/login"); }}>
                                        Sign in
                                    </button>
                                    <button className="btn btn-primary text-white w-full" onClick={() => { toggleMobileMenu(); navigate("/signup"); }}>
                                        Get Started For Free
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NavBar;
